import { defineStore } from 'pinia';
import { ref } from 'vue';
import { logger } from '@/services/loggerService';

export const useGlobalLoadingStore = defineStore('globalLoading', () => {
  // State
  const isLoading = ref(false);
  const loadingTitle = ref('Loading...');
  const loadingSubtitle = ref('');
  const minDisplayTime = ref(3000);
  
  // Stack to handle nested loading calls
  const loadingStack = ref([]);
  
  /**
   * Show the global loading spinner
   * @param {string} title - The title to display
   * @param {string} subtitle - The subtitle to display
   * @param {number} minTime - Minimum display time in milliseconds
   * @returns {string} - A unique ID for this loading operation
   */
  function showLoading(title = 'Loading...', subtitle = '', minTime = 3000) {
    const loadingId = `loading-${Date.now()}-${Math.random()}`;
    
    // Add to stack
    loadingStack.value.push({
      id: loadingId,
      title,
      subtitle,
      minTime,
      startTime: Date.now()
    });
    
    // Update display with the latest loading info
    loadingTitle.value = title;
    loadingSubtitle.value = subtitle;
    minDisplayTime.value = minTime;
    isLoading.value = true;
    
    logger.info('[GlobalLoading] Showing loading spinner', {
      id: loadingId,
      title,
      subtitle,
      stackSize: loadingStack.value.length
    });
    
    return loadingId;
  }
  
  /**
   * Hide the global loading spinner
   * @param {string} loadingId - The ID of the loading operation to hide
   */
  function hideLoading(loadingId = null) {
    if (loadingId) {
      // Remove specific loading from stack
      const index = loadingStack.value.findIndex(item => item.id === loadingId);
      if (index > -1) {
        const removed = loadingStack.value.splice(index, 1)[0];
        
        // Check if minimum display time has passed
        const elapsed = Date.now() - removed.startTime;
        const remaining = Math.max(0, removed.minTime - elapsed);
        
        if (remaining > 0) {
          // Wait for minimum time before hiding
          setTimeout(() => {
            checkAndHideLoading(loadingId);
          }, remaining);
          return;
        }
      }
    } else {
      // Clear all loading operations
      loadingStack.value = [];
    }
    
    checkAndHideLoading(loadingId);
  }
  
  /**
   * Check if loading should be hidden based on stack
   */
  function checkAndHideLoading(loadingId) {
    // If stack is empty, hide the spinner
    if (loadingStack.value.length === 0) {
      isLoading.value = false;
      logger.info('[GlobalLoading] Hiding loading spinner', {
        id: loadingId,
        stackEmpty: true
      });
    } else {
      // Update display with the latest item in stack
      const latest = loadingStack.value[loadingStack.value.length - 1];
      loadingTitle.value = latest.title;
      loadingSubtitle.value = latest.subtitle;
      minDisplayTime.value = latest.minTime;
      
      logger.info('[GlobalLoading] Updating loading spinner', {
        id: loadingId,
        remainingStack: loadingStack.value.length,
        currentTitle: latest.title
      });
    }
  }
  
  /**
   * Force hide all loading operations
   */
  function forceHideAll() {
    loadingStack.value = [];
    isLoading.value = false;
    logger.info('[GlobalLoading] Force hiding all loading spinners');
  }
  
  return {
    // State
    isLoading,
    loadingTitle,
    loadingSubtitle,
    minDisplayTime,
    loadingStack,
    
    // Actions
    showLoading,
    hideLoading,
    forceHideAll
  };
});